import { createContext, useContext, useEffect, useState } from "react";
import type { ReactNode } from "react";
import { Alert, Button, Group, Text } from "@mantine/core";
import { collection, onSnapshot, query, where } from "firebase/firestore";
import { db } from "@/config/firebase";
import { COLLECTIONS } from "@/firestore/collections";
import { RelatedLink } from "./RelatedLink";

const RefundAssociations = createContext<Record<string, string>>({});

/** Page-scoped join only. Receiving credits keep their original IDs, values and categories. */
export function LedgerRefundAssociations({
  ledgerIds,
  children,
}: {
  ledgerIds: string[];
  children: ReactNode;
}) {
  const idsKey = JSON.stringify([...new Set(ledgerIds.filter(Boolean))].sort());
  const [links, setLinks] = useState<Record<string, string>>({});
  const [error, setError] = useState(false);
  const [revision, setRevision] = useState(0);
  useEffect(() => {
    const ids = JSON.parse(idsKey) as string[];
    setLinks({});
    setError(false);
    if (!ids.length) return;
    const chunks: string[][] = [];
    for (let start = 0; start < ids.length; start += 30) chunks.push(ids.slice(start, start + 30));
    const results = new Map<number, Record<string, string>>();
    const unsubscribes = chunks.map((chunk, index) =>
      onSnapshot(
        query(
          collection(db, COLLECTIONS.cardRefunds),
          where("fulfillmentLedgerEntry", "in", chunk),
        ),
        (snapshot) => {
          const matches: Record<string, string> = {};
          for (const doc of snapshot.docs) {
            const refund = doc.data();
            if (
              refund.fulfillmentState === "completed" &&
              typeof refund.fulfillmentLedgerEntry === "string"
            ) {
              matches[refund.fulfillmentLedgerEntry] = doc.id;
            }
          }
          results.set(index, matches);
          setLinks(Object.assign({}, ...results.values()) as Record<string, string>);
        },
        () => setError(true),
      ),
    );
    return () => unsubscribes.forEach((unsubscribe) => unsubscribe());
  }, [idsKey, revision]);

  return (
    <RefundAssociations.Provider value={links}>
      {error ? (
        <Alert color="orange" mb="sm">
          <Group justify="space-between">
            <Text size="sm">
              Refund associations could not be loaded. The original ledger entries remain visible.
            </Text>
            <Button size="xs" variant="default" onClick={() => setRevision((r) => r + 1)}>
              Retry refund lookup
            </Button>
          </Group>
        </Alert>
      ) : null}
      {children}
    </RefundAssociations.Provider>
  );
}

export function LedgerRefundLink({ ledgerId }: { ledgerId: string | null | undefined }) {
  const links = useContext(RefundAssociations);
  const id = ledgerId ? links[ledgerId] : undefined;
  return id ? (
    <RelatedLink entity="cardRefund" id={id} />
  ) : (
    <Text size="sm" c="dimmed">
      —
    </Text>
  );
}
